import React, { useState, useEffect, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { workAssignmentAPI, userAPI, leaveAPI, holidayAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import BranchSelector from './BranchSelector';
import SearchableEmployeeSelect from './SearchableEmployeeSelect';
import { FiInfo, FiAlertTriangle } from './Icons';
import styled from 'styled-components';

const Form = styled.form`
  max-width: 600px;
  margin: 20px auto;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: bold;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
`;

const Button = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 8px;

  &:hover {
    background-color: #0056b3;
  }
`;

const CancelButton = styled(Button)`
  background-color: #6c757d;

  &:hover {
    background-color: #5a6268;
  }
`;

const ErrorText = styled.span`
  color: red;
  font-size: 14px;
`;

const InfoText = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  font-style: italic;
`;

const WarningText = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 8px 12px;
  margin-top: 8px;
  color: #856404;
  font-size: 14px;
  
  &.conflict {
    background: #f8d7da;
    border-color: #f5c6cb;
    color: #721c24;
  }
  
  &.info {
    background: #d1ecf1;
    border-color: #bee5eb;
    color: #0c5460;
  }
  
  &.legacy {
    font-size: 12px;
    color: #ff6b35;
    margin-top: 4px;
    font-weight: bold;
    background: none;
    border: none;
    padding: 0;
  }
`;

const WorkAssignmentForm = ({ assignment, onCancel, onSave, defaultDate, defaultBranchId }) => {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [existingAssignments, setExistingAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Helper function to format date for input[type="date"]
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Set default values including props
  const getDefaultValues = () => {
    if (assignment) {
      return {
        ...assignment,
        assignment_date: formatDateForInput(assignment.assignment_date)
      };
    }
    
    return {
      user_id: '',
      branch_id: defaultBranchId || '',
      assignment_date: defaultDate ? formatDateForInput(defaultDate) : '',
      shift_type: 'day',
      role: 'store_manager'
    };
  };
  
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm({
    defaultValues: getDefaultValues()
  });

  const selectedRole = watch('role');
  const selectedBranchId = watch('branch_id');
  const selectedDate = watch('assignment_date');

  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch leave requests and holidays when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchLeaveRequests(selectedDate);
      fetchHolidays(selectedDate);
      fetchExistingAssignments(selectedDate);
    }
  }, [selectedDate]);

  // Reset user selection when role changes
  useEffect(() => {
    if (selectedRole) {
      setValue('user_id', '');
    }
  }, [selectedRole, setValue]);

  // Reset user selection when branch changes (for fixed location employees)
  useEffect(() => {
    const currentUserId = watch('user_id');
    if (currentUserId && selectedBranchId) {
      const filteredUsers = getFilteredUsers();
      const selectedUser = filteredUsers.find(u => u.id === parseInt(currentUserId));
      if (selectedUser && selectedUser.isGrayed) {
        // Optionally keep the selection but warn user
      }
    }
  }, [selectedBranchId]);

  // Set default values when props change
  useEffect(() => {
    if (defaultDate) {
      setValue('assignment_date', formatDateForInput(defaultDate));
    }
    if (defaultBranchId) {
      setValue('branch_id', defaultBranchId);
    }
  }, [defaultDate, defaultBranchId, setValue]);

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getAll();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchLeaveRequests = async (date) => {
    if (!date) return;
    
    try {
      const selectedDate = new Date(date);
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      
      const response = await leaveAPI.getLeaveRequests({
        year: year,
        month: month,
        status: 'approved'
      });
      
      setLeaveRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      setLeaveRequests([]);
    }
  };

  const fetchHolidays = async (date) => {
    if (!date) return;
    
    try {
      const selectedDate = new Date(date);
      const year = selectedDate.getFullYear();
      
      const response = await holidayAPI.getAll({
        year: year
      });
      
      setHolidays(response.data || []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      setHolidays([]);
    }
  };

  const fetchExistingAssignments = async (date) => {
    if (!date) return;
    
    try {
      const dateStr = getLocalDateString(date);
      
      // Fetch all work assignments for the selected date across all branches
      const response = await workAssignmentAPI.getAll({
        assignment_date: dateStr
      });
      
      setExistingAssignments(response.data || []);
    } catch (error) {
      console.error('Error fetching existing assignments:', error);
      setExistingAssignments([]);
    }
  };

  // Helper function to get date string in YYYY-MM-DD format
  const getLocalDateString = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Check if user has leave on selected date
  const getUserLeaveStatus = (userId) => {
    if (!selectedDate || !leaveRequests.length) return null;
    
    const dateStr = getLocalDateString(selectedDate);
    const userLeave = leaveRequests.find(leave => 
      leave.user_id === userId && leave.leave_date === dateStr
    );
    
    return userLeave;
  };

  // Check if user has holiday on selected date based on their group
  const getUserHolidayStatus = (user) => {
    if (!selectedDate || !holidays.length || !user.holiday_group) return null;
    
    const dateStr = getLocalDateString(selectedDate);
    const userHoliday = holidays.find(holiday => 
      holiday.date === dateStr && 
      holiday.holiday_group_id === user.holiday_group.id
    );
    
    return userHoliday;
  };

  // Check if user is already assigned to a different branch on the selected date
  const getUserBranchAssignmentStatus = (userId) => {
    if (!selectedDate || !existingAssignments.length || !selectedBranchId) return null;
    
    const dateStr = getLocalDateString(selectedDate);
    const currentBranchId = parseInt(selectedBranchId);
    
    // Find assignments for this user on this date
    const userAssignments = existingAssignments.filter(assignment => 
      assignment.user_id === userId && 
      assignment.assignment_date === dateStr
    );
    
    // Check if any assignment is on a different branch
    const conflictingAssignment = userAssignments.find(assignment => 
      assignment.branch_id !== currentBranchId
    );
    
    return conflictingAssignment;
  };

  // Map simplified role categories to actual roles (base + supervisor)
  const getRolesForCategory = (category) => {
    const roleMap = {
      'manager': ['store_manager', 'manager_assistant'],
      'makeup': ['makeup', 'makeup_supervisor'],
      'photographer': ['photographer', 'photographer_supervisor'],
      'retoucher': ['retoucher', 'retoucher_supervisor']
    };
    return roleMap[category] || [category];
  };

  // Filter users based on selected role and add location information
  const getFilteredUsers = () => {
    if (!selectedRole) return [];
    
    const allowedRoles = getRolesForCategory(selectedRole);
    const roleFilteredUsers = users.filter(user => allowedRoles.includes(user.role));
    const currentBranchId = selectedBranchId ? parseInt(selectedBranchId) : null;
    
    return roleFilteredUsers.map(user => {
      const userHomeBranchId = user.home_branch_id || user.home_branch?.id;
      const isGrayed = !user.is_moveable && userHomeBranchId && currentBranchId && userHomeBranchId !== currentBranchId;
      
      // Check for leave and holiday conflicts
      const leaveStatus = getUserLeaveStatus(user.id);
      const holidayStatus = getUserHolidayStatus(user);
      const branchAssignmentStatus = getUserBranchAssignmentStatus(user.id);
      
      // Create base display name with home location and moveable status
      const homeBranchName = user.home_branch?.name || user.home_branch_name || (user.home_branch_id && `Branch ${user.home_branch_id}`) || 'Unknown';
      const moveableText = user.is_moveable ? 'Moveable' : 'fix';
      let displayName = `${user.name} (${homeBranchName} - ${moveableText})`;
      
      let isDisabled = false;
      let conflictReason = '';
      let hasHolidayWarning = false;
      
      if (leaveStatus) {
        displayName += ` (${leaveStatus.leave_type?.name || 'Leave'})`;
        isDisabled = true;
        conflictReason = `On ${leaveStatus.leave_type?.name || 'leave'}`;
      } else if (holidayStatus) {
        // Holiday: Allow assignment but show warning
        displayName += ` ⚠️ (Holiday - Group ${user.holiday_group?.name || 'Unknown'})`;
        hasHolidayWarning = true;
        conflictReason = `Holiday: ${holidayStatus.name} - Overtime assignment`;
      } else if (branchAssignmentStatus) {
        displayName += ` (Assigned on ${branchAssignmentStatus.branch?.name || 'Branch'})`;
        isDisabled = true;
        conflictReason = `Already assigned to ${branchAssignmentStatus.branch?.name || 'another branch'}`;
      }
      
      return {
        ...user,
        isInHomeLocation: !userHomeBranchId || userHomeBranchId === currentBranchId,
        displayName: displayName,
        isGrayed: isGrayed,
        isDisabled: isDisabled,
        conflictReason: conflictReason,
        hasHolidayWarning: hasHolidayWarning,
        leaveStatus: leaveStatus,
        holidayStatus: holidayStatus,
        branchAssignmentStatus: branchAssignmentStatus
      };
    });
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Validate that the selected user is not on leave or unavailable
      const selectedUser = getFilteredUsers().find(u => u.id === parseInt(data.user_id));
      if (selectedUser && selectedUser.isDisabled) {
        alert(`Cannot assign ${selectedUser.name}: ${selectedUser.conflictReason}`);
        setLoading(false);
        return;
      }
      
      // Show holiday warning but allow assignment (for overtime)
      if (selectedUser && selectedUser.hasHolidayWarning) {
        const confirmOT = window.confirm(
          `⚠️ HOLIDAY OVERTIME ASSIGNMENT\n\n` +
          `Employee: ${selectedUser.name}\n` +
          `Holiday: ${selectedUser.holidayStatus.name}\n` +
          `Date: ${getLocalDateString(selectedDate)}\n\n` +
          `This employee will be working on a public holiday (overtime).\n\n` +
          `Do you want to proceed with this assignment?`
        );
        
        if (!confirmOT) {
          setLoading(false);
          return;
        }
      }
      
      // Get the actual user role (not the category)
      const actualUser = users.find(u => u.id === parseInt(data.user_id));
      if (!actualUser) {
        alert('Selected employee not found');
        setLoading(false);
        return;
      }
      
      const transformedData = {
        ...data,
        user_id: parseInt(data.user_id),
        branch_id: parseInt(data.branch_id),
        role: actualUser.role  // Use actual user role, not category
      };

      let response;
      if (assignment) {
        response = await workAssignmentAPI.update(assignment.id, transformedData);
      } else {
        response = await workAssignmentAPI.create(transformedData);
      }

      // Check if the request was successful
      if (response && (response.status === 200 || response.status === 201)) {
        onSave();
      } else {
        throw new Error('Unexpected response status');
      }
    } catch (error) {
      console.error('Error saving assignment:', error);
      
      // Check if it's a network error but the assignment might have been saved
      if (error.message === 'Network Error' || error.code === 'ECONNABORTED') {
        // Network error - assignment might have been saved
        alert('Network timeout occurred. The assignment may have been saved. Please refresh the page to check.');
        // Still call onSave to refresh the calendar
        onSave();
      } else {
        // Handle other types of errors
        let errorMessage = 'Unknown error occurred';
        if (error.response?.data?.detail) {
          if (Array.isArray(error.response.data.detail)) {
            errorMessage = error.response.data.detail.map(d => d.msg || d).join(', ');
          } else {
            errorMessage = error.response.data.detail;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        alert(`Error saving assignment: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const canManageAssignments = currentUser && ['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor'].includes(currentUser.role);

  if (!canManageAssignments) {
    return <div>You don't have permission to manage work assignments.</div>;
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <h3>{assignment ? 'Edit Assignment' : 'Create Assignment'}</h3>
      
      <FormGroup>
        <Label>Role *</Label>
        <Select {...register('role', { required: 'Role is required' })}>
          <option value="">Select role first</option>
          <option value="manager">Manager</option>
          <option value="makeup">Makeup</option>
          <option value="photographer">Photographer</option>
          <option value="retoucher">Retoucher</option>
        </Select>
        {errors.role && <ErrorText>{errors.role.message}</ErrorText>}
      </FormGroup>

      <FormGroup>
        <Label>Employee *</Label>
        <SearchableEmployeeSelect
          employees={getFilteredUsers()}
          value={watch('user_id')}
          onChange={(e) => setValue('user_id', e.target.value, { shouldValidate: true })}
          placeholder={selectedRole ? `Search for ${selectedRole}...` : 'Select role first'}
          disabled={!selectedRole}
          required={true}
          displayFn={(user) => user.displayName}
        />
        {errors.user_id && <ErrorText>{errors.user_id.message}</ErrorText>}
        {selectedRole && getFilteredUsers().length === 0 && (
          <ErrorText>No employees found with role: {selectedRole}</ErrorText>
        )}
        
        {/* Show employee location info */}
        {selectedRole && selectedBranchId && (
          <InfoText>
            <FiInfo style={{ marginRight: '4px' }} />Employees marked "(moveable)" can work at any branch. 
            Fixed location employees appear grayed when not in their home branch but can still be assigned.
          </InfoText>
        )}
        
        {/* Show warning for fixed location employees in wrong branch */}
        {(() => {
          const currentUserId = watch('user_id');
          if (currentUserId && selectedBranchId) {
            const selectedUser = getFilteredUsers().find(u => u.id === parseInt(currentUserId));
            if (selectedUser && selectedUser.isGrayed) {
              const homeBranchName = selectedUser.home_branch?.name || 'Unknown';
              return (
                <WarningText className="legacy">
                  <FiAlertTriangle style={{ marginRight: '4px' }} />{selectedUser.name} is a fixed location employee. 
                  Their home branch is "{homeBranchName}".
                </WarningText>
              );
            }
          }
          return null;
        })()}

        {/* Show conflict warning for users on leave or holiday */}
        {(() => {
          const currentUserId = watch('user_id');
          if (currentUserId && selectedDate) {
            const selectedUser = getFilteredUsers().find(u => u.id === parseInt(currentUserId));
            if (selectedUser && selectedUser.isDisabled) {
              return (
                <WarningText className="conflict">
                  🚫 {selectedUser.name} is not available on this date: {selectedUser.conflictReason}
                </WarningText>
              );
            }
          }
          return null;
        })()}
      </FormGroup>

      <FormGroup>
        <Label>Branch *</Label>
        <BranchSelector
          value={watch('branch_id')}
          onChange={(branchId) => setValue('branch_id', branchId)}
        />
        {errors.branch_id && <ErrorText>{errors.branch_id.message}</ErrorText>}
      </FormGroup>

      <FormGroup>
        <Label>Date *</Label>
        <Input
          {...register('assignment_date', { required: 'Date is required' })}
          type="date"
        />
        {errors.assignment_date && <ErrorText>{errors.assignment_date.message}</ErrorText>}
      </FormGroup>

      <FormGroup>
        <Label>Shift *</Label>
        <Select {...register('shift_type', { required: 'Shift is required' })}>
          <option value="day">Day Shift</option>
          <option value="noon">Noon Shift</option>
        </Select>
        {errors.shift_type && <ErrorText>{errors.shift_type.message}</ErrorText>}
      </FormGroup>

      <div>
        <Button type="submit" disabled={loading}>
          {loading ? 'Saving...' : (assignment ? 'Update' : 'Create')}
        </Button>
        <CancelButton type="button" onClick={onCancel} disabled={loading}>
          Cancel
        </CancelButton>
      </div>
    </Form>
  );
};

export default WorkAssignmentForm;
